import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { BookingForm } from '../components/BookingForm';
import { Phone, MessageSquare, CheckCircle, ArrowRight, Key, Clock, Shield, Car } from 'lucide-react';
import { businessInfo, services } from '../mock';

export const KeyServices = () => {
  const service = services.find(s => s.id === 'key-services');
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const keyTypes = [
    {
      icon: Key,
      title: "Transponder Keys",
      description: "Full programming for immobilizer keys"
    },
    {
      icon: Key,
      title: "Remote Key Fobs",
      description: "Replacement and programming of all remotes"
    },
    {
      icon: Key,
      title: "Spare Keys",
      description: "Additional keys cut and coded"
    },
    {
      icon: Key,
      title: "Proximity Keys",
      description: "Keyless entry system programming"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/90 via-slate-900/90 to-blue-950/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full backdrop-blur-sm mb-6">
              <Key className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-semibold">Auto Locksmith Services</span>
            </div>

            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
              {service.title}
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Lost, broken, or need spare keys? We provide professional key cutting and programming services for all vehicle makes and models.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setIsBookingOpen(true)}
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-blue-500/50"
              >
                <span>Request Callback</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href={`tel:${businessInfo.phone}`}
                className="flex items-center justify-center space-x-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all border border-slate-700"
              >
                <Phone className="w-5 h-5" />
                <span>{businessInfo.phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Types Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              All Types of Car Keys
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              We handle all modern and traditional automotive key systems
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyTypes.map((keyType, idx) => {
              const Icon = keyType.icon;
              return (
                <div key={idx} className="p-6 bg-slate-800 rounded-xl hover:bg-slate-750 transition-all group">
                  <div className="p-3 bg-blue-600 rounded-lg w-fit mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{keyType.title}</h3>
                  <p className="text-gray-400">{keyType.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-blue-600 rounded-2xl blur-3xl opacity-20"></div>
              <img 
                src="https://images.pexels.com/photos/842528/pexels-photo-842528.jpeg"
                alt="Car Keys"
                className="relative rounded-2xl shadow-2xl"
              />
            </div>

            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-white mb-6">
                What's Included
              </h2>
              <div className="space-y-4">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-3 p-4 bg-slate-900 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">{feature}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Choose SM Engineering?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-8 bg-slate-800 rounded-xl text-center">
              <div className="p-4 bg-blue-600 rounded-full w-fit mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Fast Service</h3>
              <p className="text-gray-400">Most keys cut and programmed same day</p>
            </div>

            <div className="p-8 bg-slate-800 rounded-xl text-center">
              <div className="p-4 bg-blue-600 rounded-full w-fit mx-auto mb-4">
                <Car className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">All Makes & Models</h3>
              <p className="text-gray-400">We work with every vehicle manufacturer</p>
            </div>

            <div className="p-8 bg-slate-800 rounded-xl text-center">
              <div className="p-4 bg-blue-600 rounded-full w-fit mx-auto mb-4">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Guaranteed Work</h3>
              <p className="text-gray-400">Professional service with warranty</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-950 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Need a Replacement Key?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Get back on the road quickly with our professional key services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsBookingOpen(true)}
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all hover:scale-105 shadow-lg shadow-blue-500/50"
            >
              <span>Request Callback</span>
              <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href={`tel:${businessInfo.phone}`}
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all border border-slate-700"
            >
              <Phone className="w-5 h-5" />
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Booking Modal */}
      <BookingForm
        service={service}
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
      />
    </div>
  );
};

export default KeyServices;
